import React from 'react';
import { connect } from 'react-redux';
import { Card, Table, Button, Input, Modal, Badge, Tag, message } from 'antd';
import Icon, { CloudDownloadOutlined, DeleteOutlined, LoadingOutlined, CloseCircleTwoTone } from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import axios from '../../helpers/axios';
import showError from '../../helpers/errorlist';
import { task as LANG } from '../../helpers/lan';
import { moduleList, categoryEnum, moduleEnum } from '../../helpers/modules';

const SCard = styled(Card)`
  margin: 10em auto;
  width: 120em;
  /* min-width: 105em; */
  max-width: 100vw;
`;

const aiReportStatusEnum = {
  NO_REPORT: 0,
  CREATING: 1,
  DONE: 2,
  FAILED: 3,
};

const DownloadIcon = styled(CloudDownloadOutlined)`
  font-size: larger;
  color: ${(props) => (props.lan === 'tr' ? 'palevioletred' : '#1890ff')};
`;

const { Search } = Input;

const AiReportIcon = () => (
  <Icon
    component={() => (
      <svg xmlns="http://www.w3.org/2000/svg" width="1.0009765625em" height="1em" viewBox="0 0 1025 1024">
        <path fill="currentColor" d="M896.428 1024h-768q-53 0-90.5-37.5T.428 896V128q0-53 37.5-90.5t90.5-37.5h768q53 0 90.5 37.5t37.5 90.5v768q0 53-37.5 90.5t-90.5 37.5m-384-640q0-53-37.5-90.5t-90.5-37.5h-64q-53 0-90.5 37.5t-37.5 90.5v352q0 13 9.5 22.5t22.5 9.5t22.5-9.5t9.5-22.5V592q0-7 4.5-11.5t11.5-4.5h160q7 0 11.5 4.5t4.5 11.5v144q0 13 9.5 22.5t22.5 9.5t22.5-9.5t9.5-22.5zm288-64q13 0 22.5-9.5t9.5-22.5t-9.5-22.5t-22.5-9.5h-128q-13 0-22.5 9.5t-9.5 22.5t9.5 22.5t22.5 9.5h32v384h-32q-13 0-22.5 9.5t-9.5 22.5t9.5 22.5t22.5 9.5h128q13 0 22.5-9.5t9.5-22.5t-9.5-22.5t-22.5-9.5h-32V320zm-368 192h-160q-7 0-11.5-4.5t-4.5-11.5V384q0-26 18.5-45t45.5-19h64q26 0 45 19t19 45v112q0 7-4.5 11.5t-11.5 4.5" />
      </svg>
    )}
  />
);

class ReportCard extends React.Component {
  state = {
    reportList: [],   
  }

  componentDidMount() {
    if (this.timer) clearInterval(this.timer);
    this.loadList();
    this.timer = setInterval(() => {
      this.loadList();
    }, 10000);
    this.retryCount = 3;
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  loadList = async () => {
    let api = '/auth/reports';
    const { searchValue } = this;
    if (searchValue) {
      api = `${api}?searchValue=${searchValue}`;
    }
    try {
      const res = await axios.get(api);
      const { reportList } = res.data;
      this.setState({ reportList });
      this.retryCount = 3;
    } catch (error) {
      this.retryCount -= 1;
      if (this.timer && this.retryCount === 0) {
        showError(new Error('10010'), this.props.lan);
        clearInterval(this.timer);
      }
    }
  }

  handleSearch = (value) => {
    this.searchValue = value;
    this.loadList();
  }

  onDownload = async (record, category, lan) => {
    let requestLan = lan;
    // ONLY ZH
    if (category === categoryEnum.MC || record.module === moduleEnum.BH || record.module === moduleEnum.GP || category === categoryEnum.GE) {
      requestLan = 'zh';
    } 
    const suffix = (category === categoryEnum.CFDA && record.reportVersion === '1.0') ? '_cfda' : '';
    const res = await axios.get(`/auth/downloadurls?lan=${requestLan}&number=${record.number}&reportName=${record.reportName}${suffix}`);
    const { downloadUrl: url } = res.data;

    if ('ActiveXObject' in window) {
      document.write(`<a id='LINK${record.number}' href='${url}' style='display:none'>a</a>`);
      document.getElementById(`LINK${record.number}`).click();
    } else {
      window.open(url);
    }
  }

  onDelete = async (record) => {
    const { lan } = this.props;
    const LAN = LANG[lan];
    Modal.confirm({
      title: LAN.deleteBtn,
      content: `${LAN.deleteContent} ${record.number}?`,
      onOk: async () => {
        const res = await axios.patch('/auth/tasks', {
          id: record._id,
        });
        if (res?.data?.message === 'success') {
          message.success(LAN.successMsg);
          this.loadList();
        } else {
          message.error(LAN.failedMsg);
        }
      },
    });
  }

  onDownloadAiReport = async (record, lan) => {
    const res = await axios.get(`/auth/download-ai-report-urls?lan=${lan}&number=${record.number}&reportName=${record.reportName}`);
    const { downloadUrl: url } = res.data;

    if ('ActiveXObject' in window) {
      document.write(`<a id='LINK${record.number}' href='${url}' style='display:none'>a</a>`);
      document.getElementById(`LINK${record.number}`).click();
    } else {
      window.open(url);
    }
  }

  render() {
    const { lan, modules } = this.props;
    const LAN = LANG[lan];
    const { reportList } = this.state;
    const columns = [
      {
        title: LAN.numberTitle,
        dataIndex: 'number',
        key: 'number',
        render: (value, record) => {
          if (record.isResearchData) return (<span>{value} <Tag color="blue" title={LAN.researchDataText} key={`numtag-${value}`}>R</Tag></span>);
          return value;
        },
      
        width: 170,
        sorter: (a, b) => (a.number >= b.number ? 1 : -1),
        // sortOrder: sortedInfo.columnKey === 'Number' && sortedInfo.order,
      }, {
        title: LAN.statusTitle,
        dataIndex: 'status',
        key: 'status',
        width: 130,
        filters: [
          {
            text: LAN.waitingStatus,
            value: '0',
          }, {
            text: LAN.runningStatus,
            value: '1',
          }, {
            text: LAN.successStatus,
            value: '2',
          }, {
            text: LAN.failedStatus,
            value: '3',
          }, {
            text: LAN.expiredStatus,
            value: '4',
          }, {
            text: LAN.deletedStatus,
            value: '5',
          }, 
        ],
        onFilter: (value, record) => record.status.toString() === value,
        render: (value) => {
          switch (value) {
            case 0: return <Badge status="warning" text={LAN.waitingStatus} />;
            case 1: return <Badge status="processing" text={LAN.runningStatus} />;
            case 2: return <Badge status="success" text={LAN.successStatus} />;
            case 3: return <Badge status="error" text={LAN.failedStatus} />;
            case 4: return <Badge color="orange" text={LAN.expiredStatus} />;
            case 5: return <Badge color="magenta" text={LAN.deletedStatus} />;
            default: return '';
          }
        },
      }, {
        title: LAN.moduleTitle,
        dataIndex: 'module',
        key: 'module',
        width: 200,
        filters: Object.keys(moduleList).map((key) => ({
          text: moduleList[key][lan],
          value: key,
        })),
        onFilter: (value, record) => record.module.toString() === value,
        render: (value) => moduleList[value][lan],
      }, {
        title: LAN.reportVersion,
        dataIndex: 'reportVersion',
        key: 'reportVersion',
        sorter: (a, b) => (a.reportVersion >= b.reportVersion ? 1 : -1),
        ellipsis: true,
      // }, {
      //   title: LAN.attributeTitle,
      //   dataIndex: 'isResearchData',
      //   key: 'isResearchData',
      //   filters: [{
      //     text: LAN.researchDataText,
      //     value: true,
      //   }],
      //   onFilter: (value, record) => record.isResearchData === value,
      //   render: (value) => value && <Badge color="skyblue" text={LAN.researchDataText} />,
      }, {
        title: LAN.commentTitle,
        dataIndex: 'comment',
        key: 'comment',
        ellipsis: true,
      }, {
        title: LAN.pIdTitle,
        dataIndex: 'patientId',
        key: 'patientId',
        ellipsis: true,
      }, {
        title: LAN.pNameTitle,
        dataIndex: 'patientName',
        key: 'patientName',
        ellipsis: true,
      }, {
        title: LAN.pGenderTitle,
        dataIndex: 'patientGender',
        key: 'patientGender',
        width: 80,
      }, {
        title: LAN.pAgeTitle,
        dataIndex: 'patientAge',
        key: 'patientAge',
        width: 60,
      }, {
        title: LAN.scanTimeTitle,
        dataIndex: 'scanTime',
        key: 'scanTime',
        // width: 120,
        render: (value) => dayjs(value).format('YYYY-MM-DD'),
        // sorter: (a, b) => (a.startTime > b.startTime ? 1 : -1),
        // sortOrder: sortedInfo.columnKey === 'StartTime' && sortedInfo.order,
        // }, {
        //   title: LAN.reportTimeTitle,
        //   dataIndex: 'endTime',
        //   key: 'endTime',
        //   width: 170,
        //   sorter: (a, b) => (a.endTime > b.endTime ? 1 : -1),
        //   // sortOrder: sortedInfo.columnKey === 'CreationTime' && sortedInfo.order,
      }, {
        title: LAN.actionTitle,
        key: 'action',
        width: 150,
        render: (value, record) => {
          // AI Report
          if (moduleList[record.module].ai) {
            let AiIcon = null;
            if (record.aiReportStatus === aiReportStatusEnum.CREATING) {
              AiIcon = (
                <Button
                  type="link"
                  icon={<LoadingOutlined />}
                  title={LAN.aiCreatingBtn}
                  />
                );
            }
            if (record.aiReportStatus === aiReportStatusEnum.DONE) {
              AiIcon = (
                <Button
                  type="link"
                  icon={<AiReportIcon />}
                  title={LAN.aiReportBtn}
                  onClick={() => { this.onDownloadAiReport(record, moduleList[record.module]?.ai?.includes(lan) ? lan : 'zh'); }}
                  />
                );
            }
            if (record.aiReportStatus === aiReportStatusEnum.FAILED) {
              AiIcon = (
                <Button
                  type="link"
                  icon={<CloseCircleTwoTone />}
                  title={LAN.aiReportFailedBtn}
                  />
                );
            }
            return (
              <span>
                <Button
                  type="link"
                  icon={<DownloadIcon lan={lan} />}
                  onClick={() => { this.onDownload(record, record.category, lan); }}
                  title={LAN.reportLink}
              />
                <Button
                  type="link"
                  icon={<DeleteOutlined />}
                  title={LAN.deleteBtn}
                  onClick={() => { this.onDelete(record); }}
              />
                {AiIcon}               
              </span>
            );            
          }

          // IC Chinese Traditional
          if ((record.module === moduleEnum.ICB || record.module === moduleEnum.ICP || record.module === moduleEnum.SRE) && lan === 'zh') {
            return (
              <span>
                <Button
                  type="link"
                  icon={<DownloadIcon lan="zh" />}
                  onClick={() => { this.onDownload(record, record.category, lan); }}
                  title="下载简体中文版报告"
                />
                <Button
                  type="link"
                  icon={<DownloadIcon lan="tr" />}
                  onClick={() => { this.onDownload(record, record.category, 'tr'); }}
                  title="下載繁體中文版報告"
                />
                <Button
                  type="link"
                  icon={<DeleteOutlined />}
                  title={LAN.deleteBtn}
                  onClick={() => { this.onDelete(record); }}
            />
              </span>
            );
          }

          // Others
          return (
            <span>
              <Button
                type="link"
                icon={<DownloadIcon lan={lan} />}
                onClick={() => { this.onDownload(record, record.category, lan); }}
                title={LAN.reportLink}
            />
              <Button
                type="link"
                icon={<DeleteOutlined />}
                title={LAN.deleteBtn}
                onClick={() => { this.onDelete(record); }}
            />
            </span>
          );
        },
      },
    ];

    // 为部分模块优化
    const categories = Object.keys(modules || {});
    if (categories.length === 1 && categories[0] === '600') {
      columns.splice(3, 3);
    } else if (categories.every((v) => v !== '200')) {
      columns.splice(4, 1);
    }
    
    return (
      <SCard
        title={LAN.reportCardTitle}
        extra={(
          <Search
            placeholder={LAN.reportSearchPlaceholder}
            onSearch={this.handleSearch}
            enterButton
            allowClear
          />
        )}
      >
        <Table
          columns={columns}
          dataSource={reportList}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1050 }}
        />
      </SCard>
    );
  }
}

const mapStateToProps = (state) => ({
  lan: state.lan,
  username: state.username,
  modules: state.modules,
});

export default connect(mapStateToProps)(ReportCard);
